import React, { useEffect, useState } from 'react';
import { DatabaseIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { StepProps, TITLE_PROFILE_MAP } from '../../types/onboarding';
type TextField = 'firstName' | 'lastName' | 'alias' | 'email' | 'username' | 'title' | 'phone' | 'mobile' | 'fax' | 'extension' | 'ringCentralExtensionId' | 'bonzoId' | 'nmlsNumber' | 'lendingPadUserId' | 'applicationLink';
const ROLES = ['CEO', 'VP of Sales', 'Division Lead', 'Team Lead', 'Loan Officer', 'Jr. Loan Officer', 'Processing Director', 'Processor Manager', 'Processor', 'Jr Processor', 'Solution Desk', 'IT', 'HR', 'Training'];
const PROFILES = Array.from(new Set(Object.values(TITLE_PROFILE_MAP))).sort();
const TEAM_LEADS = ['Timothy Young', 'Sarah Mitchell', 'James Park', 'Lisa Torres', 'Kevin Wright', 'Amy Foster'];
const ENHANCED_NOTE_ROLES = ['Standard', 'Manager', 'Admin', 'Read Only'];
const inputClass = 'w-full border-0 border-b border-gray-300 rounded-none px-0 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-navy-600 focus:ring-0 bg-transparent transition-colors disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed';
const selectClass = `${inputClass} appearance-none cursor-pointer pr-6`;
const labelClass = 'block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 whitespace-nowrap truncate';
export function UserDetails({
  data,
  updateData,
  setCanAdvance
}: StepProps) {
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {
    const isFormValid = !!(data.firstName.trim() && data.lastName.trim() && data.alias.trim() && data.email.trim() && data.username.trim() && data.role && data.profile);
    setCanAdvance(isFormValid);
  }, [data, setCanAdvance]);
  const handleNameChange = (field: 'firstName' | 'lastName', value: string) => {
    const updates: Partial<typeof data> = {
      [field]: value
    };
    const first = field === 'firstName' ? value : data.firstName;
    const last = field === 'lastName' ? value : data.lastName;
    if (first && last && !data.alias) {
      updates.alias = `${first.charAt(0).toUpperCase()}${last.substring(0, 4)}`;
    }
    updateData(updates);
  };
  const handleEmailChange = (email: string) => {
    const updates: Partial<typeof data> = {
      email
    };
    if (!data.username || data.username === data.email) {
      updates.username = email;
    }
    updateData(updates);
  };
  const handleTitleChange = (title: string) => {
    const mappedProfile = TITLE_PROFILE_MAP[title];
    updateData({
      title,
      ...(mappedProfile && !data.profile ? {
        profile: mappedProfile
      } : {})
    });
  };
  const text = (field: TextField, label: string, options: {
    required?: boolean;
    type?: string;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    onChange?: (value: string) => void;
  } = {}) => <div className={options.className} data-id="element-411" data-mp="3p2ysjaapqnfc">
      <label htmlFor={field} className={labelClass} data-id="element-412" data-mp="3p41j1h44inuq">
        {label}
        {options.required && <span className="text-red-500" data-id="element-413" data-mp="3kaynj3fsd4bw"> *</span>}
      </label>
      <input id={field} type={options.type ?? 'text'} value={data[field]} disabled={options.disabled} onChange={e => options.onChange ? options.onChange(e.target.value) : updateData({
      [field]: e.target.value
    })} className={inputClass} placeholder={options.placeholder} data-id="element-414" data-mp="3fcdubnyybkwy" />
    </div>;
  const select = (field: 'role' | 'profile' | 'enhancedNoteRole' | 'teamLead', label: string, choices: string[], placeholder: string, required = false) => <div className="relative" data-id="element-415" data-mp="3u3w8dg8935do">
      <label htmlFor={field} className={labelClass} data-id="element-416" data-mp="2mohtc72cn6ud">
        {label}
        {required && <span className="text-red-500" data-id="element-417" data-mp="2immr90clgpfj"> *</span>}
      </label>
      <select id={field} value={data[field]} onChange={e => updateData({
      [field]: e.target.value
    })} className={`${selectClass} ${data[field] ? '' : 'text-gray-400'}`} data-id="element-418" data-mp="2il39147do4b4">
        <option value="" data-id="element-419" data-mp="f6hv9aq4ynju">{placeholder}</option>
        {choices.map(c => <option key={c} value={c} className="text-gray-900" data-id="element-420" data-mp="h3li1ine43x">
            {c}
          </option>)}
      </select>
      <svg className="pointer-events-none absolute right-0 bottom-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-421" data-mp="5ciry8ebsxqb">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-422" data-mp="k3hyzezije7s" />
      </svg>
    </div>;
  const accountName = data.firstName && data.lastName ? `${data.firstName} ${data.lastName} Database` : 'New User Database';
  return <div data-id="element-423" data-mp="34sfkknu3dedd">
      {/* Identity */}
      <Section title="Identity & Login" first data-id="element-424" data-mp="38v2f4wi5dde9">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6" data-id="element-425" data-mp="38wvncg8es5od">
          {text('firstName', 'First Name', {
          required: true,
          placeholder: 'Jane',
          onChange: v => handleNameChange('firstName', v)
        })}
          {text('lastName', 'Last Name', {
          required: true,
          placeholder: 'Doe',
          onChange: v => handleNameChange('lastName', v)
        })}
          {text('alias', 'Alias', {
          required: true,
          placeholder: 'JDoe'
        })}
          {text('email', 'Email', {
          required: true,
          type: 'email',
          placeholder: 'jane.doe@activelinkmortgage.com',
          onChange: handleEmailChange
        })}
          {text('username', 'Username', {
          required: true,
          type: 'email',
          placeholder: 'jane.doe@activelinkmortgage.com'
        })}
          <div data-id="element-426" data-mp="1zt92m52rfz21">
            <label htmlFor="defaultPassword" className={labelClass} data-id="element-427" data-mp="1zspluroujgpl">
              Default Password
            </label>
            <div className="relative" data-id="element-428" data-mp="2kjuvbea4xuc9">
              <input id="defaultPassword" type={showPassword ? 'text' : 'password'} value={data.defaultPassword} onChange={e => updateData({
              defaultPassword: e.target.value
            })} className={`${inputClass} pr-8`} placeholder="Temporary password" autoComplete="new-password" data-id="element-429" data-mp="2kitgqztwupps" />
              <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute right-0 bottom-2 p-0.5 text-gray-400 hover:text-gray-600 transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-430" data-mp="21mbw12dhbq31">
                {showPassword ? <EyeOffIcon className="w-4 h-4" data-id="element-431" data-mp="3m0uaj5p7ipaj" /> : <EyeIcon className="w-4 h-4" data-id="element-432" data-mp="3m2yd9kbddi98" />}
              </button>
            </div>
          </div>
        </div>
      </Section>

      {/* Role & Reporting */}
      <Section title="Role & Reporting" data-id="element-433" data-mp="1fc22i6uv6920">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6" data-id="element-434" data-mp="1adidtyovyj5y">
          {text('title', 'Title', {
          placeholder: 'Mortgage Loan Officer',
          onChange: handleTitleChange
        })}
          {select('role', 'Role', ROLES, 'Select role', true)}
          {select('profile', 'Profile', PROFILES, 'Select profile', true)}
          {select('enhancedNoteRole', 'Enhanced Note Role', ENHANCED_NOTE_ROLES, 'Select role')}
          {select('teamLead', 'Team Lead', TEAM_LEADS, 'Select team lead')}
        </div>
      </Section>

      {/* Contact */}
      <Section title="Contact" data-id="element-435" data-mp="hq4n4ibs6jua">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6" data-id="element-436" data-mp="hr7f6tkuasbs">
          {text('phone', 'Phone', {
          type: 'tel',
          placeholder: '714.798.9050'
        })}
          {text('mobile', 'Mobile', {
          type: 'tel',
          placeholder: '470.554.2280'
        })}
          {text('fax', 'Fax', {
          type: 'tel',
          placeholder: '714.798.9050'
        })}
          {text('extension', 'Extension', {
          placeholder: '256'
        })}
        </div>
      </Section>

      {/* Integrations & Licensing */}
      <Section title="Integrations & Licensing" data-id="element-437" data-mp="1c4oogdv8eogq">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6" data-id="element-438" data-mp="1g6jolf1fevsx">
          {text('nmlsNumber', 'NMLS #', {
          placeholder: '2843932'
        })}
          {text('lendingPadUserId', 'LendingPad User Id', {
          placeholder: 'LendingPad Id'
        })}
          {text('ringCentralExtensionId', 'RingCentral Ext. (Mailbox) Id', {
          placeholder: '885596018'
        })}

          <div data-id="element-439" data-mp="x7nstz9x69cz">
            <span className={labelClass} data-id="element-440" data-mp="x75rio8eh4y7">Bonzo User</span>
            <label className="flex items-center gap-3 py-1.5 cursor-pointer w-fit" data-id="element-441" data-mp="x9qtdd8p7kvy">
              <button type="button" role="switch" aria-checked={data.isBonzoUser} aria-label="Bonzo User" onClick={() => updateData({
              isBonzoUser: !data.isBonzoUser
            })} className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-600 focus-visible:ring-offset-2 ${data.isBonzoUser ? 'bg-navy-600' : 'bg-gray-200'}`} data-id="element-442" data-mp="2ni8xrvksba42">
                <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ease-out ${data.isBonzoUser ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-443" data-mp="zl9icq1lmd2q" />
              </button>
              <span className="text-sm text-gray-600" data-id="element-444" data-mp="13ofn1t92bw73">
                {data.isBonzoUser ? 'Yes' : 'No'}
              </span>
            </label>
          </div>
          {text('bonzoId', 'Bonzo Id', {
          placeholder: data.isBonzoUser ? '151622' : 'Enable Bonzo User first',
          disabled: !data.isBonzoUser
        })}
          <div className="hidden lg:block" aria-hidden="true" data-id="element-445" data-mp="2q5b3ufdy8cl" />

          {text('applicationLink', 'Application Link', {
          type: 'url',
          placeholder: 'https://',
          className: 'sm:col-span-2 lg:col-span-3'
        })}
        </div>
      </Section>

      {/* Account Preview */}
      <div className="mt-10 bg-gray-50 rounded-lg p-4 flex items-center space-x-4 border border-gray-100" data-id="element-446" data-mp="3kwjo9r5tye9x">
        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100" data-id="element-447" data-mp="lf23fsr82qs6">
          <DatabaseIcon className="w-5 h-5 text-navy-600" data-id="element-448" data-mp="cna0e34j2jxm" />
        </div>
        <div data-id="element-449" data-mp="clffgot9artj">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5" data-id="element-450" data-mp="7ogi7xeaq4r4">
            Account Record to be created
          </p>
          <p className="text-sm font-semibold text-gray-900" data-id="element-451" data-mp="154mc937b7z07">{accountName}</p>
        </div>
      </div>
    </div>;
}
function Section({
  title,
  first = false,
  children
}: {
  title: string;
  first?: boolean;
  children: React.ReactNode;
}) {
  return <section className={first ? '' : 'mt-10'} data-id="element-452" data-mp="1cn3oviahftxe">
      <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-5" data-id="element-453" data-mp="1co547yyj72kx">
        {title}
      </h3>
      {children}
    </section>;
}